/*
  QUICKSHOP MODAL.

  Opens a Free People style quick shop over the collection grid. The body is fetched on demand
  from sections/quickshop.liquid via the Section Rendering API, so the modal costs nothing until
  it is opened and never duplicates the product page's markup or ids.

  ORDER OF THE OPEN SEQUENCE IS LOAD-BEARING — fetch -> inject -> show -> initPages:

    1. fetch    /products/{handle}?section_id=quickshop
    2. inject   into the content container with innerHTML. Custom elements upgrade automatically
                on insertion, so media-gallery, slider-component, variant-selects and product-form
                self-construct here — PROVIDED their defining scripts are already on the page.
                innerHTML does NOT execute injected <script> tags, so the fetched section is not
                relied on to bring its own JS; the collection section loads media-gallery.js.
    3. show     ModalDialog.show() runs trapFocus, which must not run before the content exists or
                it holds stale nodes.
    4. initPages  SliderComponent filters its slides with `clientWidth > 0`. Constructing while the
                modal is still display:none yields zero items and an early return, so the gallery
                arrows are recomputed only once the panel is actually laid out.

  On close the content container is emptied. The injected snippets emit scoped <style> blocks
  (price.liquid, buy-buttons.liquid, rating-stars-block.liquid) which would otherwise accumulate
  one set per open.

  ADD TO CART is handled here rather than by the theme's <product-form>. The theme's form opens the
  cart drawer on success, which this modal must not do, and its implementation lives in a remote
  obfuscated bundle that cannot be patched. The click is intercepted in the CAPTURE phase on the
  modal element — capture runs top-down from the modal, so stopping propagation there prevents the
  event ever reaching <product-form> or the <form> below it, and no native submit is fired at all.
*/
(function () {
  if (typeof ModalDialog === 'undefined') {
    console.error('quickshop: ModalDialog is not defined (secondary.js missing?)');
    return;
  }
  if (customElements.get('quickshop-modal')) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /*
    How long the bundle skeleton waits before deciding no deal block is coming.

    This also now governs when the quantity fallback appears, so 6000ms would have left a bundle-less
    product showing a shimmering skeleton for six seconds before its quantity control arrived.
    Kaching's two Storefront round trips measure 206-381ms on this shop, so 1200ms keeps roughly 3x
    headroom.

    Lowering it cannot affect a product that HAS a bundle: that case settles through the
    MutationObserver the moment the app paints and never reaches this timer. The original reason for
    a long timeout — a late block collapsing the slot and reflowing the panel — no longer applies
    either, because the panel now has a minimum height holding it steady.
  */
  var BUNDLE_TIMEOUT_MS = 1200;
  // The drawer opens asynchronously after Kaching's cart request resolves.
  var DRAWER_GUARD_INTERVAL_MS = 100;
  var DRAWER_GUARD_TICKS = 80; // 8s
  var CART_POLL_INTERVAL_MS = 250;
  var CART_POLL_TIMEOUT_MS = 8000;

  customElements.define(
    'quickshop-modal',
    class QuickshopModal extends ModalDialog {
      constructor() {
        // ModalDialog's constructor requires [id^="ModalClose-"] and binds Escape / backdrop.
        super();

        this.content = this.querySelector('[data-quickshop-content]');
        this.cartIconSlot = this.querySelector('[data-quickshop-cart-icon]');
        this.cartBubble = this.querySelector('[data-quickshop-cart-bubble]');
        this.inFlight = false;

        this.adoptHeaderCartIcon();

        // See the header comment: capture phase, on the modal, so the theme's product-form never
        // sees the click.
        this.addEventListener('click', this.onCaptureClick.bind(this), true);
        this.addEventListener('keydown', this.onRatingKeydown.bind(this));
      }

      /*
        SPACE ON THE RATING ROW.

        The row is an <a href>, matching View Full Details, so it keeps real-link behaviour --
        same-tab navigation, middle-click, copy address, and Enter for free. Anchors do not activate
        on Space; buttons do. This adds Space without giving up the anchor, and touches nothing
        else: any other key, and any target that is not the rating link, falls straight through.
      */
      onRatingKeydown(event) {
        if (event.key !== ' ' && event.key !== 'Spacebar') return;
        if (!event.target || !event.target.closest) return;
        var link = event.target.closest('.quickshop__info-inner > .rating-stars > a');
        if (!link) return;
        // Space would otherwise scroll the panel underneath the activation.
        event.preventDefault();
        link.click();
      }

      /* ------------------------------- open / close ------------------------------- */

      openQuickshop(handle, opener) {
        if (!handle || this.inFlight) return;
        this.inFlight = true;
        this._handle = handle;
        // Cleared per open, so the previous product's delivered ratio cannot size this one's first
        // paint. Until its own image loads, Liquid's master-asset value is used.
        this._deliveredRatio = 0;
        this._ratioLock = null;
        this.classList.add('quickshop-modal--loading');
        if (opener) opener.setAttribute('aria-busy', 'true');

        fetch(ROOT + 'products/' + encodeURIComponent(handle) + '?section_id=quickshop')
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then(
            function (html) {
              // 2. inject
              this.content.innerHTML = html;
              this.removeGalleryFavoritesBadge();
              // Before show(), so the merged figure is the first thing painted rather than a
              // visible jump from the metafield-only value.
              this.refineRatingStars();
              // 3. show
              this.show(opener);
              // 4. initPages
              this.initGallery();
              // 5. watch the Kaching slot so the skeleton clears (or collapses) on its own.
              this.watchBundleSlot();
              // 6. size the image column from the right column's height, and keep it in step.
              this.watchMediaWidth();
              // 7. re-derive the ratio from the file the browser actually loaded. After (6), so
              //    the Liquid value has already produced a correctly-shaped first paint.
              this.lockDeliveredRatio();
            }.bind(this)
          )
          .catch(function (error) {
            console.error('quickshop: failed to open -', error);
          })
          .finally(
            function () {
              this.inFlight = false;
              this.classList.remove('quickshop-modal--loading');
              if (opener) opener.removeAttribute('aria-busy');
            }.bind(this)
          );
      }

      hide() {
        this.stopWatchingBundleSlot();
        this.stopWatchingMediaWidth();
        super.hide();
        // Must happen after super.hide(): removeTrapFocus() restores focus to the opener and would
        // otherwise be reaching into nodes that have already been destroyed.
        this.content.innerHTML = '';
      }

      /* ------------------------------- kaching bundles ------------------------------- */

      /*
        The <kaching-bundle> placeholder is server-rendered empty. On a collection page the app
        embed has no inline deal data (its config carries "productId": null), so it needs two
        Storefront round trips before anything paints — measured at roughly a third of a second.
        The slot therefore ships with a skeleton and reserves that space, so the modal does not
        reflow under the shopper when the block lands.

        Kaching finds the placeholder on its own: it runs a MutationObserver on document.body for
        added KACHING-BUNDLE elements and, with reinitialize_morphed_placeholders enabled for this
        shop, re-runs its placeholder initialisation. Verified working — no explicit
        window.kachingBundlesInitialize() call is made, so the app keeps ownership of its own
        lifecycle and we do not risk double-initialising it.

        This observer only drives the SKELETON. It flips the slot to `ready` the moment the app
        renders anything, and to `empty` if nothing has arrived by the timeout — which is the
        no-configured-bundle case, and collapses the slot to nothing.
      */
      watchBundleSlot() {
        this.stopWatchingBundleSlot();

        var slot = this.content.querySelector('[data-quickshop-bundle-slot]');
        if (!slot) return;
        var placeholder = slot.querySelector('kaching-bundle');
        if (!placeholder) {
          slot.dataset.state = 'empty';
          return;
        }

        var settle = function (state) {
          if (slot.dataset.state === state) return;
          slot.dataset.state = state;
          // `ready` is terminal. `empty` is not: the observer stays connected so a block arriving
          // after the hard stop can still promote the slot, rather than rendering inside a
          // collapsed one where it would be invisible.
          if (state === 'ready') this.stopWatchingBundleSlot();
        }.bind(this);

        var check = function () {
          if (placeholder.children.length > 0) settle('ready');
        };

        this._bundleObserver = new MutationObserver(check);
        this._bundleObserver.observe(placeholder, { childList: true, subtree: true });

        // Hard stop: no deal block for this product, the app failing, or the Storefront calls
        // erroring all land here, and all of them must leave no box and no gap.
        this._bundleTimer = window.setTimeout(function () {
          settle(placeholder.children.length > 0 ? 'ready' : 'empty');
        }, BUNDLE_TIMEOUT_MS);

        check();
      }

      stopWatchingBundleSlot() {
        if (this._bundleObserver) {
          this._bundleObserver.disconnect();
          this._bundleObserver = null;
        }
        if (this._bundleTimer) {
          window.clearTimeout(this._bundleTimer);
          this._bundleTimer = null;
        }
      }

      // A deal block has actually rendered, so Kaching owns the add-to-cart click.
      hasActiveBundle() {
        var placeholder = this.content.querySelector('kaching-bundle');
        return !!(placeholder && placeholder.children.length > 0 && placeholder.offsetHeight > 0);
      }

      /* ------------------------------- injected content ------------------------------- */

      // product-media-gallery renders its own favourites badge, positioned by media-gallery.js and
      // carrying the product page's per-template offset. The quickshop renders its own badge next
      // to the gallery with the offset resolved from the theme-level lookup, so the internal one is
      // removed rather than left as a second, wrongly-numbered trigger for the same product.
      removeGalleryFavoritesBadge() {
        this.content.querySelectorAll('media-gallery .product-favorites-badge').forEach(function (badge) {
          badge.remove();
        });
      }

      /*
        rating-stars-block renders the ORGANIC (Judge.me metafield) rating only. The product page
        shows a merged figure that also includes the reviews section's imported testimonials and
        rating offsets, which live in the product template and are unreachable from Liquid here —
        sections/quickshop.liquid resolves them through snippets/reviews-stats.liquid and hands the
        merged result down on data-review-average / data-review-count.

        The mutation below is deliberately byte-for-byte what refineExternalStars() in
        assets/reviews-section.js does on the product page, so both surfaces land identically. That
        file is not modified; this is the same public DOM contract it targets.
      */
      refineRatingStars() {
        var root = this.content.querySelector('[data-quickshop-root]');
        if (!root) return;

        var average = parseFloat(root.dataset.reviewAverage);
        var count = parseInt(root.dataset.reviewCount, 10);
        if (isNaN(average) || isNaN(count)) return;

        var rounded = Math.round(average * 10) / 10;
        this.content.querySelectorAll('[data-rating-source="reviews"]').forEach(function (el) {
          el.style.setProperty('--rating', String(rounded));
          el.style.setProperty('--visible-stars', String(Math.max(1, Math.ceil(rounded))));
          var label = el.querySelector('.rating-stars__label');
          var template = el.dataset.labelTemplate;
          if (label && template) {
            label.innerHTML =
              '&nbsp;' +
              template.replace(/\[avg\]/g, rounded.toFixed(1)).replace(/\[count\]/g, String(count));
          }
        });
      }

      /*
        IMAGE COLUMN SIZING.

        The image must fill the panel's height without being cropped or distorted, which means its
        WIDTH has to follow that height at the image's own aspect ratio. No pure-CSS construct can
        express that here: the height is set by the right column, and both flex and grid resolve a
        row's inline size before its block size, so `aspect-ratio` on the image column resolves
        against a not-yet-known height and collapses to zero (measured: 0px at every viewport).

        So the height is measured and the width published as a custom property. A ResizeObserver on
        the right column keeps it correct as content changes — which it does on this modal, because
        the Kaching bundle block arrives a few hundred milliseconds after open and makes the column
        taller.

        The value is clamped to the room the panel's own cap leaves. Past that point the image stops
        growing and is centred in its column by CSS rather than being cropped.
      */
      syncMediaWidth() {
        var grid = this.content.querySelector('[data-quickshop-root]');
        var media = this.content.querySelector('.quickshop__media');
        var info = this.content.querySelector('.quickshop__info');
        var panel = this.querySelector('.quickshop-modal__panel');
        if (!grid || !media || !info || !panel) return;

        // Below the two-column breakpoint the columns stack and the width is CSS's business.
        if (!window.matchMedia('(min-width: 750px)').matches) {
          grid.style.removeProperty('--qs-media-w');
          return;
        }

        /*
          The DELIVERED file's ratio once it is known, the Liquid value until then. See
          lockDeliveredRatio() for why the two differ and why the first paint has to use Liquid's.
        */
        var ratio =
          this._deliveredRatio ||
          parseFloat(getComputedStyle(grid).getPropertyValue('--qs-image-ratio')) ||
          0.75;
        var styles = getComputedStyle(grid);
        var gap = parseFloat(styles.columnGap) || 0;
        var infoWidth = info.getBoundingClientRect().width;

        /*
          The cap, resolved here rather than read off the panel: the panel's max-width is a min()
          expression whose computed value is not a reliable px number to parse, and getting this
          wrong lets the image column overrun the viewport (measured: 919px of image inside a 946px
          modal at a 990px viewport). Both terms are computed from live boxes instead.
        */
        var modalStyles = getComputedStyle(this);
        var viewportRoom =
          this.getBoundingClientRect().width -
          (parseFloat(modalStyles.paddingLeft) || 0) -
          (parseFloat(modalStyles.paddingRight) || 0);
        var configured = parseFloat(getComputedStyle(grid).getPropertyValue('--qs-max-width-px'));
        var cap = isNaN(configured) ? viewportRoom : Math.min(configured, viewportRoom);

        var available = Math.max(0, cap - infoWidth - gap);
        var wanted = info.getBoundingClientRect().height * ratio;
        var next = Math.min(wanted, available);

        grid.style.setProperty('--qs-media-w', next.toFixed(2) + 'px');
      }

      watchMediaWidth() {
        this.stopWatchingMediaWidth();
        var info = this.content.querySelector('.quickshop__info');
        if (!info) return;
        this.syncMediaWidth();
        this._mediaObserver = new ResizeObserver(this.syncMediaWidth.bind(this));
        this._mediaObserver.observe(info);
        this._mediaResize = this.syncMediaWidth.bind(this);
        window.addEventListener('resize', this._mediaResize);
      }

      /*
        THE IMAGE RATIO COMES FROM THE FILE THE BROWSER ACTUALLY LOADED.

        sections/quickshop.liquid emits --qs-image-ratio from the MASTER asset (1792x2400 =
        0.746667). The browser never loads the master: it picks a srcset candidate whose integer
        dimensions round to a slightly different ratio (715x957 = 0.747126). So the column is built
        to the master's shape while `object-fit: contain` letterboxes the file that actually
        painted -- about a quarter of a pixel, top and bottom. At DPR 2 that half device pixel
        antialiases into a faint pale line along the image's edge.

        Re-reading naturalWidth/naturalHeight after load gives the delivered file's true ratio, so
        the box and its contents agree exactly and the letterbox goes to zero.

        LOCKED ONCE, DELIBERATELY. This does NOT track the displayed image. Re-deriving on arrow
        navigation or colour filtering would resize the panel on every click, which is exactly what
        the height floor exists to prevent. The first loaded image wins and the value never moves
        again for the life of the open modal.
      */
      lockDeliveredRatio() {
        /*
          The active slide, falling back to the first. Two queries rather than one selector list,
          because querySelector resolves a comma-separated list in DOCUMENT order, not in the order
          the selectors are written -- so a single list would silently return the first slide even
          when a later one is the active one.
        */
        var img =
          this.content.querySelector('#MediaGallery-quickshop .product__media-item.is-active img') ||
          this.content.querySelector('#MediaGallery-quickshop .product-media-container .media > img');
        if (!img) return;

        var self = this;
        var first = 0;
        var pass = 0;

        function read() {
          // hide() empties the content, so a queued frame can arrive after the node is gone.
          if (!img.isConnected || !img.naturalWidth || !img.naturalHeight) return;
          var ratio = img.naturalWidth / img.naturalHeight;
          pass += 1;

          if (pass === 1) {
            first = ratio;
            self._deliveredRatio = ratio;
            self.syncMediaWidth();
            /*
              OSCILLATION GUARD. Recomputing the width can move the browser onto a different
              srcset candidate, whose integer dimensions give a different ratio again. Two passes
              total -- read, compute, re-read once, accept, stop. Two frames so the new width is
              laid out and any candidate swap has resolved before the second read. Never a loop.
            */
            window.requestAnimationFrame(function () {
              window.requestAnimationFrame(read);
            });
            return;
          }

          // Pass two is final. Whatever the browser settled on is accepted as-is.
          self._ratioLock = { locked: ratio, first: first, secondPassDiffered: ratio !== first };
          if (ratio !== first) {
            self._deliveredRatio = ratio;
            self.syncMediaWidth();
          }
          self.warnOnDivergentRatios(ratio);
        }

        // A cached image is already complete and will never fire `load`.
        if (img.complete) read();
        else img.addEventListener('load', read, { once: true });
      }

      /*
        Reports gallery images whose shape disagrees with the locked one -- they will letterbox,
        because the column is sized for the first image only. Reported, never acted on: resizing
        per image is the behaviour this file deliberately does not have.

        The tolerance is there because srcset rounding alone moves the ratio in the fourth decimal
        (715x957 = 0.747126 against 900x1205 = 0.746888) and that is not a real divergence. A
        genuine one -- 3:4 against 4:5 -- is two orders of magnitude larger. Images that have not
        been loaded yet report 0 and are skipped.
      */
      warnOnDivergentRatios(locked) {
        var images = this.content.querySelectorAll('#MediaGallery-quickshop .product__media-item img');
        for (var i = 0; i < images.length; i++) {
          var image = images[i];
          if (!image.naturalWidth || !image.naturalHeight) continue;
          var ratio = image.naturalWidth / image.naturalHeight;
          if (Math.abs(ratio - locked) > 0.005) {
            console.warn(
              'quickshop: gallery image ratio differs from the locked ratio -',
              this._handle,
              '| locked', locked,
              '| image', ratio,
              image.currentSrc || image.src
            );
          }
        }
      }

      stopWatchingMediaWidth() {
        if (this._mediaObserver) {
          this._mediaObserver.disconnect();
          this._mediaObserver = null;
        }
        if (this._mediaResize) {
          window.removeEventListener('resize', this._mediaResize);
          this._mediaResize = null;
        }
      }

      initGallery() {
        var viewer = this.content.querySelector('slider-component[id^="GalleryViewer"]');
        if (!viewer) return;

        var refresh = function () {
          try {
            if (viewer.initPages) viewer.initPages();
            if (viewer.update) viewer.update();
          } catch (error) {
            console.error('quickshop: gallery slider refresh failed -', error);
          }
        };

        // Synchronously (the panel is laid out the moment `open` is set) and again on the next
        // frame, in case a transition on the panel delays its final width.
        refresh();
        window.requestAnimationFrame(refresh);
      }

      /* ------------------------------- add to cart ------------------------------- */

      /*
        WHEN A BUNDLE IS LIVE, KACHING OWNS THE ADD.

        Kaching registers its own CAPTURE-phase click listener directly on the submit button. This
        listener sits on the modal, an ancestor, so capture reaches it first — and the
        stopImmediatePropagation() below would stop the event ever arriving at the button, killing
        Kaching's handler. Measured consequence: the selected tier was ignored and the cart got one
        unit at full price with no discount and no __kaching_bundles line property.

        So when a deal block has actually rendered, this listener stands aside and lets the click
        through. Kaching then builds the correct multi-line request itself — verified: quantity 2,
        final_price 8498 against price 9995, discount "Duo", line property present.

        Two things still have to be handled here, because we no longer own that request:
          - Shrine's cart drawer activates off the resulting cart change. The modal must not open
            it, so it is closed through cart-drawer's own public close() method.
          - the header and modal cart bubbles were previously refreshed from our own response's
            `sections` payload. With Kaching issuing the request, we wait for the cart to actually
            change and then re-render the bubble through the existing fallback path.

        With no bundle rendered, the original path below runs completely unchanged.
      */
      onCaptureClick(event) {
        var button = event.target.closest('#ProductSubmitButton-quickshop');
        if (!button || button.disabled) return;

        if (this.hasActiveBundle()) {
          this.suppressCartDrawer();
          this.awaitCartChange();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.addToCart(button);
      }

      /*
        Shrine's cart-drawer exposes close() on its own element, so this uses the component's
        documented contract rather than fighting it with CSS or reaching into the remote bundle.
        It is called on a short repeating tick because the drawer opens asynchronously, once the
        cart request Kaching issued comes back.
      */
      suppressCartDrawer() {
        var drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        window.clearInterval(this._drawerGuard);
        var ticks = 0;
        this._drawerGuard = window.setInterval(
          function () {
            if (drawer.classList.contains('active') || drawer.hasAttribute('open')) {
              if (typeof drawer.close === 'function') drawer.close();
              else drawer.classList.remove('active', 'animate');
            }
            if (++ticks > DRAWER_GUARD_TICKS) {
              window.clearInterval(this._drawerGuard);
              this._drawerGuard = null;
            }
          }.bind(this),
          DRAWER_GUARD_INTERVAL_MS
        );
      }

      /*
        Polls the cart until its item count moves, then re-renders the bubble. Deliberately a poll
        rather than a hook into Kaching or the theme bundle: neither exposes a cart-changed event we
        can rely on, and guessing at one would be the kind of private-API coupling that breaks on an
        app update. Bounded, and gives up quietly.
      */
      awaitCartChange() {
        var self = this;
        fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json();
          })
          .then(function (before) {
            var started = Date.now();
            var poll = function () {
              if (Date.now() - started > CART_POLL_TIMEOUT_MS) return;
              fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
                .then(function (response) {
                  return response.json();
                })
                .then(function (now) {
                  if (now.item_count !== before.item_count) {
                    self.refreshCartBubble();
                    return;
                  }
                  window.setTimeout(poll, CART_POLL_INTERVAL_MS);
                })
                .catch(function () {});
            };
            window.setTimeout(poll, CART_POLL_INTERVAL_MS);
          })
          .catch(function (error) {
            console.error('quickshop: could not read the cart before a bundle add -', error);
          });
      }

      addToCart(button) {
        var form = this.content.querySelector('form[data-type="add-to-cart-form"]');
        var variantInput = form && form.querySelector('.product-variant-id');
        if (!variantInput || !variantInput.value) return;

        // Deliberately NOT a FormData of the form: the variant option radios associate with the
        // form by `form=` attribute and would be posted as unknown params.
        //
        // `sections` uses the theme's existing pattern — layout/theme.liquid already asks
        // /cart/update.js for ['cart-drawer', 'cart-icon-bubble']. Asking add.js for the bubble
        // gets the header's count re-rendered by Shopify in the same round trip, with no extra
        // request and no change to sections/header.liquid. cart-drawer is deliberately NOT
        // requested: this modal must not touch the drawer.
        /*
          Quantity comes from the fallback control when it is VISIBLE, which by construction is
          only when no bundle rendered. With a bundle present Kaching owns the add entirely and
          this path is not reached, but the visibility guard keeps that true regardless.
        */
        var qty = 1;
        var qtySelect = this.content.querySelector('[data-quickshop-qty]');
        if (qtySelect && qtySelect.offsetParent !== null) {
          qty = Math.max(1, parseInt(qtySelect.value, 10) || 1);
        }

        var payload = {
          items: [{ id: Number(variantInput.value), quantity: qty }],
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        };

        button.setAttribute('disabled', 'disabled');
        this.setError('');

        fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(
            function (result) {
              if (!result.ok) throw new Error(result.data.description || result.data.message || 'Add to cart failed');
              this.confirmAdd(button);
              var sections = result.data.sections || {};
              if (typeof sections['cart-icon-bubble'] === 'string') {
                this.applyCartBubble(sections['cart-icon-bubble']);
                return;
              }
              return this.refreshCartBubble();
            }.bind(this)
          )
          .catch(
            function (error) {
              this.setError(error.message || 'Could not add to cart');
              console.error('quickshop: add to cart failed -', error);
            }.bind(this)
          )
          .finally(function () {
            button.removeAttribute('disabled');
          });
      }

      confirmAdd(button) {
        button.classList.add('quickshop-atc--added');
        window.clearTimeout(this._confirmTimer);
        this._confirmTimer = window.setTimeout(function () {
          button.classList.remove('quickshop-atc--added');
        }, 2000);
      }

      /* ------------------------------- cart indicator ------------------------------- */

      /*
        snippets/icon-cart.liquid chooses its glyph and stroke weight from `section.settings`, i.e.
        the HEADER section's settings. Rendering it from the quickshop shell would resolve those to
        nil and silently emit the fallback glyph at the wrong weight. The header has already
        rendered the correct one on this page, so the icon is cloned from there — the same node,
        guaranteed identical rather than merely similar.

        Falls back to leaving the slot empty (icon-less link) rather than drawing a different cart.
      */
      adoptHeaderCartIcon() {
        if (!this.cartIconSlot) return;
        var headerIcon = document.querySelector('.header__icon--cart .icon-cart');
        if (headerIcon) this.cartIconSlot.appendChild(headerIcon.cloneNode(true));
        // Seed the bubble from the header's current server-rendered state.
        var headerBubble = document.getElementById('cart-icon-bubble');
        if (headerBubble && this.cartBubble) this.cartBubble.innerHTML = headerBubble.innerHTML;
      }

      /*
        Writes one cart-icon-bubble render into BOTH the modal's indicator and the header's, so the
        two can never disagree and the count renders through the section's own markup and
        thresholds (no bubble at all at zero items).
      */
      applyCartBubble(html) {
        if (typeof html !== 'string') return;
        if (this.cartBubble) this.cartBubble.innerHTML = html;
        var headerBubble = document.getElementById('cart-icon-bubble');
        if (headerBubble) headerBubble.innerHTML = html;
      }

      /*
        Fallback for the rare case where /cart/add.js returns no sections payload. Uses the same
        section name theme.liquid already passes to /cart/update.js.
      */
      refreshCartBubble() {
        return fetch(ROOT + '?section_id=cart-icon-bubble')
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then(
            function (html) {
              this.applyCartBubble(html);
            }.bind(this)
          )
          .catch(function (error) {
            console.error('quickshop: cart bubble refresh failed -', error);
          });
      }

      setError(message) {
        var wrapper = this.content.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) return;
        var target = wrapper.querySelector('.product-form__error-message');
        if (target) target.textContent = message;
        wrapper.toggleAttribute('hidden', !message);
      }
    }
  );

  /*
    SHELL INSTALLATION — exactly one, however many sections shipped a template.

    snippets/quickshop-modal.liquid ships the shell inside an inert <template>, because more than
    one section on a page can render it and Liquid has no shared state for a section to check
    whether another already did. Template content is a separate DocumentFragment, so nothing in
    there upgrades or is reachable by getElementById; cloning the first one into document.body is
    what makes it live. Everything here sits inside this file's customElements.get() guard, so a
    second copy of the script returns long before reaching it.
  */
  function installShell() {
    if (document.getElementById('QuickshopModal')) return;
    var template = document.querySelector('template[data-quickshop-shell]');
    if (!template) return;
    // Upgrades <quickshop-modal> on insert; its connectedCallback then relocates to document.body,
    // which is where this already puts it.
    document.body.appendChild(template.content.cloneNode(true));
  }

  // defer means this runs after parsing, so document.body exists; the readyState branch is only
  // for a non-deferred include.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installShell, { once: true });
  } else {
    installShell();
  }

  /*
    Trigger delegation. CAPTURE phase for the same reason favorites.js uses it: the trigger sits on
    a card whose title link paints a whole-card ::after overlay, and capture guarantees this runs
    before anything bound further down the tree.
  */
  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest('[data-quickshop-trigger]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();

      var modal = document.getElementById('QuickshopModal');
      if (!modal || typeof modal.openQuickshop !== 'function') return;
      modal.openQuickshop(trigger.dataset.productHandle, trigger);
    },
    true
  );
})();
